import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useAuthInit } from '@/hooks/useAuth'
import { captureError } from '@/lib/sentry'

// ─── Query client ─────────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry:              2,
      retryDelay:         (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      staleTime:          30 * 1000,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 1,
    },
  },
})

// ─── Auth initialiser ─────────────────────────────────────────────────────────

function AuthInit({ children }: { children: ReactNode }) {
  useAuthInit()
  return <>{children}</>
}

// ─── Error boundary ───────────────────────────────────────────────────────────

interface ErrorBoundaryState { hasError: boolean; error?: Error }

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureError(error, { componentStack: info.componentStack ?? '' })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-room-bg flex items-center justify-center px-4">
          <div className="max-w-sm w-full text-center space-y-4">
            <p className="text-4xl">⚠️</p>
            <h2 className="text-white font-bold text-xl">Something went wrong</h2>
            <p className="text-room-muted text-sm">
              {this.state.error?.message ?? 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload() }}
              className="px-6 py-2.5 bg-room-accent rounded-xl text-white text-sm font-semibold hover:bg-[#c026d3] transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// ─── Root provider ────────────────────────────────────────────────────────────

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthInit>
          {children}
        </AuthInit>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
